import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { SELECT_STYLES, mergeSelectStyles } from "../../utils/selectStyles";
import {
  CButton,
  CCollapse,
  CFormCheck,
  CFormInput,
  CFormSelect,
} from "@coreui/react";
import { Tooltip } from "react-tooltip";
import * as Sentry from "@sentry/react";
import DashboardContent from "../../components/DashboardContent";
import RedirectHandler from "../../components/RedirectHandler";
import useCsr from "../../hooks/useCsr";
import { CsrAPI } from "../../apis/CsrAPI";
import QuestionItemInfo from "../../components/QuestionItemInfo";
import { ReactComponent as BinIcon } from "../../assets/images/icons/bin.svg";
import { ReactComponent as PlusIcon } from "../../assets/images/icons/plus.svg";
import { ReactComponent as CopyIcon } from "../../assets/images/icons/copy-white.svg";
import { ReactComponent as CopyOutlineIcon } from "../../assets/images/icons/copy.svg";
import {
  SCS_ADDITIONAL_GROUPING_TABLE_OPTIONS,
  SCS_COMPARISON_TYPE_OPTIONS,
  SCS_GROUPING_TABLE_OPTIONS,
  SCS_STUDY_STATUS_OPTIONS,
  SCS_STUDY_TYPE_OPTIONS,
  SCS_TREATMENT_GROUP_ROLE_OPTIONS,
} from "../../utils/scsDescriptionOfStudiesOptions";
import {
  SCS_DOS_FIELD_LABELS,
  SCS_DOS_QUESTIONS,
} from "../../utils/scsDescriptionOfStudiesQuestions";
import { useScsTableOptions } from "../../hooks/useScsTableOptions";
import { setScsDescriptionOfStudies } from "../../store/slices/scsDataSlice";

const isProduction = process.env.NODE_ENV === "production";

const FORM_ID = "scs-description-of-studies-form";
const REMOVE_TOOLTIP_ID = "scs-dos-remove";
const DUPLICATE_TOOLTIP_ID = "scs-dos-duplicate";

const emptyStudy = () => ({ qScsStudyType: "", qScsStudyStatus: "" });
const emptyGrouping = () => ({ name: "", studies: [] });
const emptyTreatmentGroup = () => ({ name: "", role: "" });
const emptyComparisonPair = () => ({ treatment: "", comparator: "" });
// UI shape of one comparison grouping block. It holds every branch answer so
// switching the comparison type (Q7) never loses what was already typed; only
// the answers belonging to the selected type are persisted (buildComparisons).
const emptySingleArm = () => ({
  qGroupingName: "",
  qDemPopulation: "",
  qComparisonType: "",
  qNonComparativeGroupNames: [""],
  qTreatmentGroupNames: [emptyTreatmentGroup()],
  qComparisonPairs: [emptyComparisonPair()],
  qDoseGroups: [""],
});

// Comparison type (Q7) -> the saved section the grouping belongs to.
const COMPARISON_TYPE_SECTION = {
  "Non-Comparative": "single_arm",
  Comparative: "multi_arm",
  "Dose-Finding": "dose_finding",
};

// Q5 holds a single grouping name; tolerate the list shape held by previously
// saved state (Q5 was briefly a multi-select) by keeping its first entry.
const asGroupingName = value =>
  (Array.isArray(value) ? value.filter(Boolean)[0] : value) || "";

const seedList = list =>
  Array.isArray(list) && list.length > 0 ? [...list] : [""];

// Treatment group roles are saved lowercase ("treatment" / "comparator") but
// shown with the wording of SCS_TREATMENT_GROUP_ROLE_OPTIONS on screen.
const roleForPayload = role => (role || "").toLowerCase();
const roleForDisplay = role =>
  SCS_TREATMENT_GROUP_ROLE_OPTIONS.find(
    option => option.toLowerCase() === (role || "").toLowerCase()
  ) || "";

// Deep-clone a comparison grouping so a duplicated block is fully independent
// from its source (mutating one never affects the other) — mirrors the CSR
// duplicateStdPkBlock behaviour. Also used to rebuild a block from saved data,
// where only the fields of the block's own section are present.
const cloneSingleArm = arm => ({
  ...emptySingleArm(),
  qGroupingName: asGroupingName(arm?.qGroupingName),
  qDemPopulation: arm?.qDemPopulation || "",
  qComparisonType: arm?.qComparisonType || "",
  qNonComparativeGroupNames: seedList(arm?.qNonComparativeGroupNames),
  qTreatmentGroupNames: (arm?.qTreatmentGroupNames?.length
    ? arm.qTreatmentGroupNames
    : [emptyTreatmentGroup()]
  ).map(tg => ({ name: tg?.name || "", role: roleForDisplay(tg?.role) })),
  qComparisonPairs: (arm?.qComparisonPairs?.length
    ? arm.qComparisonPairs
    : [emptyComparisonPair()]
  ).map(pair => ({
    treatment: pair?.treatment || "",
    comparator: pair?.comparator || "",
  })),
  qDoseGroups: seedList(arm?.qDoseGroups),
});

// --- Saved <-> UI shape for the comparison groupings ----------------------
//
// Each grouping block is saved into exactly one section, decided by Q7. Both
// single_arm and multi_arm store their group names under qTreatmentGroupNames
// as { name, role } — single_arm has no role question, so its role is always
// an empty string:
//   Non-Comparative -> single_arm   { qGroupingName, qDemPopulation,
//                                     qTreatmentGroupNames }
//   Comparative     -> multi_arm    { qGroupingName, qDemPopulation,
//                                     qTreatmentGroupNames, qComparisonPairs }
//   Dose-Finding    -> dose_finding { qGroupingName, qDemPopulation,
//                                     qDoseGroups }
// qComparisonType itself is not persisted — it is derived back from the
// section a grouping was restored from.
const buildComparisons = blocks => {
  const comparisons = { single_arm: [], multi_arm: [], dose_finding: [] };
  blocks.forEach(arm => {
    const common = {
      qGroupingName: asGroupingName(arm?.qGroupingName),
      qDemPopulation: arm?.qDemPopulation || "",
    };
    const section = COMPARISON_TYPE_SECTION[arm?.qComparisonType];
    if (section === "multi_arm") {
      comparisons.multi_arm.push({
        ...common,
        qTreatmentGroupNames: (arm.qTreatmentGroupNames || []).map(tg => ({
          ...tg,
          role: roleForPayload(tg?.role),
        })),
        qComparisonPairs: arm.qComparisonPairs || [],
      });
    } else if (section === "dose_finding") {
      comparisons.dose_finding.push({
        ...common,
        qDoseGroups: arm.qDoseGroups || [],
      });
    } else if (section === "single_arm") {
      comparisons.single_arm.push({
        ...common,
        qTreatmentGroupNames: (arm.qNonComparativeGroupNames || []).map(
          name => ({ name: name || "", role: "" })
        ),
      });
    } else {
      // No comparison type picked yet — keep the answered common questions in
      // single_arm without any branch answers, so nothing is lost on reload.
      comparisons.single_arm.push(common);
    }
  });
  return comparisons;
};

// Rebuild the UI blocks from the three saved sections. Legacy state (where
// everything, including qComparisonType, lived in single_arm) still restores
// because such entries carry qComparisonType explicitly.
const restoreComparisons = (comparisons = {}) => {
  const fromSection = (entries, comparisonType, mapEntry = entry => entry) =>
    (Array.isArray(entries) ? entries : []).map(entry => {
      const type =
        entry && "qComparisonType" in entry
          ? entry.qComparisonType
          : comparisonType(entry);
      return cloneSingleArm({
        ...mapEntry(entry, type),
        qComparisonType: type,
      });
    });

  const blocks = [
    ...fromSection(
      comparisons.single_arm,
      // A restored single_arm entry is Non-Comparative when it carries the
      // group names; otherwise Q7 was never answered.
      entry =>
        Array.isArray(entry?.qTreatmentGroupNames) ||
        Array.isArray(entry?.qNonComparativeGroupNames)
          ? "Non-Comparative"
          : "",
      // single_arm group names live under qTreatmentGroupNames but belong to
      // the Non-Comparative question on screen (legacy state saved them under
      // qNonComparativeGroupNames as plain strings).
      (entry, type) =>
        type === "Non-Comparative"
          ? {
              ...entry,
              qTreatmentGroupNames: undefined,
              qNonComparativeGroupNames:
                entry?.qNonComparativeGroupNames ||
                (entry?.qTreatmentGroupNames || []).map(tg => tg?.name || ""),
            }
          : entry
    ),
    ...fromSection(comparisons.multi_arm, () => "Comparative"),
    ...fromSection(comparisons.dose_finding, () => "Dose-Finding"),
  ];
  return blocks.length > 0 ? blocks : [emptySingleArm()];
};

const toOptions = values => (values || []).map(v => ({ value: v, label: v }));
const fromOptions = options => (options || []).map(option => option.value);

// Render a single-select's chosen value as a chip/tag so it visually matches
// the multi-select dropdowns (Q3) — look only, the control stays single-select.
const SINGLE_VALUE_CHIP_STYLES = mergeSelectStyles(SELECT_STYLES, {
  singleValue: provided => ({
    ...provided,
    display: "inline-flex",
    alignItems: "center",
    maxWidth: "calc(100% - 8px)",
    backgroundColor: "#eef1f5",
    borderRadius: "4px",
    padding: "3px 6px",
    margin: "2px",
    fontSize: "13px",
    boxSizing: "border-box",
  }),
});

// Repeatable blocks (grouping definitions + the comparison groupings) always
// keep at least one entry; the first entry is non-removable (mirrors the CSR
// wizard block pattern).
const MIN_BLOCK_ENTRIES = 1;
const seedBlock = (list, factory) =>
  Array.isArray(list) && list.length > 0 ? list : [factory()];

const SCSDescriptionOfStudies = () => {
  const { studyId: summaryId } = useParams();
  const { setAjaxLoading, wrongEnvironment } = useCsr();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Grouping-table dropdowns (Q2 / Q3) are populated from the safety-summary
  // TFL content in the scsData store once files have been PROCESSED; until
  // then we fall back to the bundled placeholder options.
  const { tableNames: scsTableNames } = useScsTableOptions();
  const groupingTableOptions = scsTableNames.length
    ? scsTableNames
    : SCS_GROUPING_TABLE_OPTIONS;
  const additionalGroupingTableOptions = scsTableNames.length
    ? scsTableNames
    : SCS_ADDITIONAL_GROUPING_TABLE_OPTIONS;

  const [studyNameOptions, setStudyNameOptions] = useState([]);
  const [questionText, setQuestionText] = useState({});
  const [studies, setStudies] = useState([emptyStudy()]);
  const [groupingTable, setGroupingTable] = useState("");
  const [additionalGroupingTables, setAdditionalGroupingTables] = useState([]);
  const [groupingDefinitions, setGroupingDefinitions] = useState([
    emptyGrouping(),
  ]);
  const [individualStudyDescriptions, setIndividualStudyDescriptions] =
    useState(false);
  const [individualStudyList, setIndividualStudyList] = useState([]);
  // One entry per comparison grouping block; split into the single_arm /
  // multi_arm / dose_finding sections on save (see buildComparisons).
  const [singleArm, setSingleArm] = useState([emptySingleArm()]);
  // Which comparison-grouping accordion items are open (by array index). Only
  // relevant when there is more than one grouping; the newly added / duplicated
  // block (always the last) is the one left expanded (mirrors Standard PK).
  const [openGroupIndexes, setOpenGroupIndexes] = useState(() => new Set([0]));
  const lastGroupRef = useRef(null);
  const shouldScrollGroupRef = useRef(false);

  const [studyErrors, setStudyErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRedirect, setShowRedirect] = useState(false);

  const studyRefs = useRef({});
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Study name options come from the same endpoint the CSR wizard uses
  // (StudySelectDropdown -> getAllStudiesForLaySummaries).
  useEffect(() => {
    CsrAPI.getAllStudiesForLaySummaries()
      .then(allStudies => {
        if (!mountedRef.current) return;
        const names = (allStudies || [])
          .map(study => study?.name)
          .filter(Boolean);
        setStudyNameOptions([...new Set(names)]);
      })
      .catch(error => {
        if (isProduction) Sentry.captureException(error);
      });
  }, []);

  // Question wording is served from the same endpoint pattern the CSR wizard
  // uses; fall back to the bundled constants when it is unavailable.
  useEffect(() => {
    CsrAPI.getSafetySummaryQuestions()
      .then(data => {
        if (mountedRef.current && data) setQuestionText(data);
      })
      .catch(error => {
        if (isProduction) Sentry.captureException(error);
      });
  }, []);

  // Pre-populate previously saved wizard state.
  useEffect(() => {
    if (!summaryId) return;

    setAjaxLoading(true);
    CsrAPI.getSafetySummaryDescriptionOfStudies({ summaryId })
      .then(data => {
        if (!mountedRef.current) return;
        const dos = data?.description_of_studies || data || {};

        // Share the saved answers with the rest of the SCS wizard (Data Mapping
        // renders one accordion per Q2A grouping name).
        dispatch(setScsDescriptionOfStudies({ summaryId, data: dos }));

        if (Array.isArray(dos.studies) && dos.studies.length > 0) {
          setStudies(
            dos.studies.map(s => ({
              qScsStudyType: s?.qScsStudyType || "",
              qScsStudyStatus: s?.qScsStudyStatus || "",
            }))
          );
        }

        const groupings = dos.groupings || {};
        setGroupingTable(groupings.qGroupingTable || "");
        setAdditionalGroupingTables(groupings.qAdditionalGroupingTables || []);
        setGroupingDefinitions(
          seedBlock(
            Object.entries(groupings.qGroupingDefinitions || {}).map(
              ([name, list]) => ({
                name,
                studies: Array.isArray(list) ? list : [],
              })
            ),
            emptyGrouping
          )
        );

        const descriptions = dos.descriptions || {};
        setIndividualStudyDescriptions(
          Boolean(descriptions.qIndividualStudyDescriptions)
        );
        setIndividualStudyList(descriptions.qIndividualStudyList || []);

        const restoredBlocks = restoreComparisons(dos.comparisons);
        setSingleArm(restoredBlocks);
        // Keep only the last grouping expanded once saved data is restored.
        setOpenGroupIndexes(new Set([restoredBlocks.length - 1]));
      })
      .catch(error => {
        if (!mountedRef.current) return;
        // A 404 simply means nothing has been saved yet — start with defaults.
        if (error?.response?.status === 404) {
          dispatch(setScsDescriptionOfStudies({ summaryId, data: null }));
          return;
        }
        if (isProduction) Sentry.captureException(error);
        if (error?.response?.status === 403) {
          setShowRedirect(true);
        } else {
          toast.error("Failed to load previously saved data.");
        }
      })
      .finally(() => {
        if (mountedRef.current) setAjaxLoading(false);
      });
  }, [summaryId, setAjaxLoading, dispatch]);

  // Scroll the newly added / duplicated comparison grouping into view. Walk up
  // to the named scroll container so we never scroll the browser viewport
  // (matches the Standard PK block behaviour).
  useEffect(() => {
    if (!shouldScrollGroupRef.current || !lastGroupRef.current) return;
    const el = lastGroupRef.current;
    let wrapper = el.parentElement;
    while (
      wrapper &&
      !wrapper.classList.contains("dashboard-content__wrapper")
    ) {
      wrapper = wrapper.parentElement;
    }
    if (wrapper) {
      const elTop = el.getBoundingClientRect().top;
      const wrapperTop = wrapper.getBoundingClientRect().top;
      wrapper.scrollBy({ top: elTop - wrapperTop, behavior: "smooth" });
    }
    shouldScrollGroupRef.current = false;
  }, [singleArm.length]);

  // --- Section A: studies -------------------------------------------------
  const addStudy = () => setStudies(prev => [...prev, emptyStudy()]);
  const removeStudy = index =>
    setStudies(prev => prev.filter((_, i) => i !== index));
  const updateStudy = (index, field, value) =>
    setStudies(prev =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );

  // Guarded removal for min-one repeatable blocks — never drop below one entry.
  const removeBlockEntry = (setter, index) =>
    setter(prev =>
      prev.length <= MIN_BLOCK_ENTRIES
        ? prev
        : prev.filter((_, i) => i !== index)
    );

  // --- Section B: groupings ----------------------------------------------
  const addGrouping = () =>
    setGroupingDefinitions(prev => [...prev, emptyGrouping()]);
  const removeGrouping = index =>
    removeBlockEntry(setGroupingDefinitions, index);
  const updateGroupingName = (index, value) =>
    setGroupingDefinitions(prev =>
      prev.map((row, i) => (i === index ? { ...row, name: value } : row))
    );
  const updateGroupingStudies = (index, values) =>
    setGroupingDefinitions(prev =>
      prev.map((row, i) => (i === index ? { ...row, studies: values } : row))
    );

  // --- Section D helpers (generic list mutators) --------------------------
  const updateArm = (setter, index, updater) =>
    setter(prev => prev.map((row, i) => (i === index ? updater(row) : row)));

  // Comparison grouping blocks (single arm) — CSR-style block controls.
  // "Add another group" appends an empty block; "Duplicate group above"
  // appends a copy of the last block; per-block "Duplicate" appends a copy of
  // that block. New blocks always go to the end and become the only expanded
  // accordion item (mirrors Standard PK).
  const appendSingleArm = block =>
    setSingleArm(prev => {
      const next = [...prev, block];
      shouldScrollGroupRef.current = true;
      setOpenGroupIndexes(new Set([next.length - 1]));
      return next;
    });
  const addSingleArm = () => appendSingleArm(emptySingleArm());
  const duplicateSingleArm = index =>
    setSingleArm(prev => {
      if (!prev[index]) return prev;
      const next = [...prev, cloneSingleArm(prev[index])];
      shouldScrollGroupRef.current = true;
      setOpenGroupIndexes(new Set([next.length - 1]));
      return next;
    });
  const duplicateSingleArmAbove = () => {
    if (!singleArm.length) return;
    duplicateSingleArm(singleArm.length - 1);
  };
  const removeSingleArm = index =>
    setSingleArm(prev => {
      if (prev.length <= MIN_BLOCK_ENTRIES) return prev;
      const next = prev.filter((_, i) => i !== index);
      // Leave the last remaining grouping expanded.
      setOpenGroupIndexes(new Set([next.length - 1]));
      return next;
    });
  const toggleGroup = index =>
    setOpenGroupIndexes(prev => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });

  const validate = () => {
    const errors = {};
    studies.forEach((row, index) => {
      const rowErrors = {};
      if (!row.qScsStudyType)
        rowErrors.qScsStudyType = "Study type is required";
      if (!row.qScsStudyStatus)
        rowErrors.qScsStudyStatus = "Study status is required";
      if (Object.keys(rowErrors).length) errors[index] = rowErrors;
    });
    setStudyErrors(errors);

    const firstInvalid = Object.keys(errors)[0];
    if (firstInvalid !== undefined) {
      const rowErrors = errors[firstInvalid];
      const field = rowErrors.qScsStudyType
        ? "qScsStudyType"
        : "qScsStudyStatus";
      studyRefs.current[`${firstInvalid}-${field}`]?.focus();
      return false;
    }
    return true;
  };

  const buildPayload = () => ({
    // description_of_studies: {
    studies: studies.map(row => ({
      qScsStudyType: row.qScsStudyType,
      qScsStudyStatus: row.qScsStudyStatus,
    })),
    groupings: {
      qGroupingTable: groupingTable || null,
      qAdditionalGroupingTables: additionalGroupingTables,
      qGroupingDefinitions: groupingDefinitions.reduce((acc, group) => {
        if (group.name) acc[group.name] = group.studies;
        return acc;
      }, {}),
    },
    descriptions: {
      qIndividualStudyDescriptions: individualStudyDescriptions,
      qIndividualStudyList: individualStudyDescriptions
        ? individualStudyList
        : [],
    },
    comparisons: buildComparisons(singleArm),
    // },
  });

  // Persists the wizard state via PATCH .../wizards/description_of_studies.
  // Shared by the "Save" button (stays on the page) and the "Subject
  // Disposition" button (navigates on success); returns whether the save
  // succeeded so the caller can decide what to do next.
  const saveWizardState = async () => {
    if (isSubmitting) return false;
    if (!validate()) {
      toast.error("Please fill in all required fields.");
      return false;
    }

    setIsSubmitting(true);
    try {
      const payload = buildPayload();
      await CsrAPI.saveSafetySummaryDescriptionOfStudies({
        summaryId,
        state: payload,
      });
      // Keep the store in step with what was just saved, so the pages reading
      // these answers (Data Mapping) pick up renamed / added / removed
      // groupings without a re-fetch.
      dispatch(setScsDescriptionOfStudies({ summaryId, data: payload }));
      return true;
    } catch (error) {
      if (isProduction) Sentry.captureException(error);
      if (mountedRef.current) {
        if (error?.response?.status === 403) {
          setShowRedirect(true);
        } else {
          toast.error("Failed to save. Please try again.");
        }
      }
      return false;
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const saved = await saveWizardState();
    if (saved && mountedRef.current) {
      navigate(`/scs/${summaryId}/subject-disposition`);
    }
  };

  const handleSave = async event => {
    event?.preventDefault();
    const saved = await saveWizardState();
    if (saved && mountedRef.current) {
      toast.success("Data saved successfully!");
    }
  };

  const stringSelectOptions = values => [
    { value: "", label: "Select..." },
    ...values.map(v => ({ value: v, label: v })),
  ];

  const removeButton = (label, onClick, disabled = false) => (
    <CButton
      color={"danger"}
      shape={"pill"}
      aria-label={label}
      data-tooltip-id={REMOVE_TOOLTIP_ID}
      data-tooltip-content={label}
      disabled={disabled}
      onClick={onClick}>
      <BinIcon />
    </CButton>
  );

  const addButton = (label, onClick, style) => (
    <CButton
      color={"primary"}
      variant={"outline"}
      className={"btn-light scs-dos__add"}
      style={style}
      onClick={onClick}>
      {label} <PlusIcon />
    </CButton>
  );

  // Solid-blue pill sitting at the top of each comparison grouping block
  // (mirrors the CSR "Duplicate" block control).
  const duplicateBlockButton = onClick => (
    <CButton
      color={"primary"}
      shape={"pill"}
      className={"scs-dos__duplicate"}
      data-tooltip-id={DUPLICATE_TOOLTIP_ID}
      data-tooltip-content={"Duplicate this grouping"}
      onClick={onClick}>
      <CopyIcon /> Duplicate
    </CButton>
  );

  // Outlined pill pairing with "Add another group" at the bottom of the
  // comparison groupings (mirrors the CSR "Duplicate block above" control).
  const duplicateAboveButton = (label, onClick) => (
    <CButton
      color={"primary"}
      variant={"outline"}
      className={"btn-light scs-dos__add"}
      onClick={onClick}>
      {label} <CopyOutlineIcon />
    </CButton>
  );

  // Endpoint wording wins; fall back to the bundled question / field text.
  const qText = key =>
    questionText[key] ||
    SCS_DOS_QUESTIONS[key]?.text ||
    SCS_DOS_FIELD_LABELS[key] ||
    "";

  // CSR-style numbered question: index badge in the left gutter, label +
  // control in the item column (mirrors QuestionItem).
  const renderQuestion = (key, control, numberOverride) => {
    const question = SCS_DOS_QUESTIONS[key];
    if (!question) return null;
    return (
      <div className={"questions-content__container"}>
        <div className={"questions-content__questionIndexContainer"}>
          <span className={"questions-content__label-index"}>
            {numberOverride ?? question.number}
          </span>
        </div>
        <div className={"questions-content__item"}>
          <div className={"questions-content__label"}>
            <span className={"questions-content__label-text"}>
              {qText(key)}
              {question.required && (
                <span className={"questions-content__required-asterisk"}>
                  {" "}
                  *
                </span>
              )}
            </span>
            {question.tooltip && (
              <QuestionItemInfo
                questionName={key}
                notes={{ [key]: question.tooltip }}
              />
            )}
          </div>
          <div className={"questions-content__input"}>{control}</div>
        </div>
      </div>
    );
  };

  // Same CSR layout for controls that have no numbered question (empty gutter).
  const renderLabeledBlock = (label, control) => (
    <div className={"questions-content__container"}>
      <div className={"questions-content__questionIndexContainer"} />
      <div className={"questions-content__item"}>
        <div className={"questions-content__label"}>
          <span className={"questions-content__label-text"}>{label}</span>
        </div>
        <div className={"questions-content__input"}>{control}</div>
      </div>
    </div>
  );

  const renderStudies = () =>
    renderQuestion(
      "qStudyClassification",
      <>
        {studies.map((row, index) => {
          const rowErrors = studyErrors[index] || {};
          return (
            <div
              key={`study-${index}`}
              className={"questions-content__input-group"}>
              <div className={"form-component"}>
                <label htmlFor={`study-type-${index}`}>
                  {qText("qStudyType")}
                </label>
                <CFormSelect
                  id={`study-type-${index}`}
                  ref={el => (studyRefs.current[`${index}-qScsStudyType`] = el)}
                  value={row.qScsStudyType}
                  options={stringSelectOptions(SCS_STUDY_TYPE_OPTIONS)}
                  invalid={Boolean(rowErrors.qScsStudyType)}
                  onChange={e =>
                    updateStudy(index, "qScsStudyType", e.target.value)
                  }
                />
                {rowErrors.qScsStudyType && (
                  <span className={"errmsg"}>{rowErrors.qScsStudyType}</span>
                )}
              </div>
              <div className={"form-component"}>
                <label htmlFor={`study-status-${index}`}>
                  {qText("qStudyStatus")}
                </label>
                <CFormSelect
                  id={`study-status-${index}`}
                  ref={el =>
                    (studyRefs.current[`${index}-qScsStudyStatus`] = el)
                  }
                  value={row.qScsStudyStatus}
                  options={stringSelectOptions(SCS_STUDY_STATUS_OPTIONS)}
                  invalid={Boolean(rowErrors.qScsStudyStatus)}
                  onChange={e =>
                    updateStudy(index, "qScsStudyStatus", e.target.value)
                  }
                />
                {rowErrors.qScsStudyStatus && (
                  <span className={"errmsg"}>{rowErrors.qScsStudyStatus}</span>
                )}
              </div>
              {studies.length === 1 ? (
                <span className={"empty-space-layout-helper"}></span>
              ) : (
                removeButton("Remove study", () => removeStudy(index))
              )}
            </div>
          );
        })}
        {addButton("Add Study", addStudy)}
      </>
    );

  const renderGroupings = () => (
    <>
      {renderQuestion(
        "qGroupingTable",
        <Select
          className={"scs-dos__control"}
          styles={SINGLE_VALUE_CHIP_STYLES}
          options={toOptions(groupingTableOptions)}
          value={
            groupingTable
              ? { value: groupingTable, label: groupingTable }
              : null
          }
          onChange={selected =>
            setGroupingTable(selected ? selected.value : "")
          }
          placeholder={"Select grouping table"}
          isClearable
        />
      )}
      {!groupingTable &&
        renderQuestion(
          "qGroupingDefinitions",
          <>
            {groupingDefinitions.map((group, index) => (
              <div
                key={`grouping-${index}`}
                className={"questions-content__input-group"}>
                <div className={"form-component scs-dos__grouping-def-field"}>
                  <label htmlFor={`grouping-name-${index}`}>
                    Grouping Name
                  </label>
                  <CFormInput
                    id={`grouping-name-${index}`}
                    value={group.name}
                    placeholder={"e.g. Non-Comp(Total)"}
                    onChange={e => updateGroupingName(index, e.target.value)}
                  />
                </div>
                <div className={"form-component scs-dos__grouping-def-field"}>
                  <label>Studies</label>
                  <Select
                    styles={SELECT_STYLES}
                    isMulti
                    className={"scs-dos__control"}
                    options={toOptions(studyNameOptions)}
                    value={toOptions(group.studies)}
                    onChange={selected =>
                      updateGroupingStudies(index, fromOptions(selected))
                    }
                    placeholder={"Select studies"}
                  />
                </div>
                {index > 0 &&
                  removeButton("Remove grouping", () => removeGrouping(index))}
              </div>
            ))}
            {addButton("Add Grouping", addGrouping)}
          </>
        )}
      {renderQuestion(
        "qAdditionalGroupingTables",
        <Select
          styles={SELECT_STYLES}
          isMulti
          className={"scs-dos__control"}
          options={toOptions(additionalGroupingTableOptions)}
          value={toOptions(additionalGroupingTables)}
          onChange={selected =>
            setAdditionalGroupingTables(fromOptions(selected))
          }
          placeholder={"Select grouping tables"}
        />
      )}
    </>
  );

  const renderDescriptions = () => (
    <>
      {renderQuestion(
        "qIndividualStudyDescriptions",
        <CFormCheck
          type={"checkbox"}
          id={"individual-study-descriptions"}
          label={"Yes"}
          checked={individualStudyDescriptions}
          onChange={e => setIndividualStudyDescriptions(e.target.checked)}
        />
      )}
      {individualStudyDescriptions &&
        renderQuestion(
          "qIndividualStudyList",
          <Select
            styles={SELECT_STYLES}
            isMulti
            options={toOptions(studyNameOptions)}
            value={toOptions(individualStudyList)}
            onChange={selected => setIndividualStudyList(fromOptions(selected))}
            placeholder={"Select studies"}
          />
        )}
    </>
  );

  const renderTreatmentGroups = (
    arm,
    index,
    setter,
    headingKey,
    numberOverride
  ) => {
    const control = (
      <>
        {arm.qTreatmentGroupNames.map((tg, tgIndex) => (
          <div
            key={`tg-${index}-${tgIndex}`}
            className={"questions-content__input-group"}>
            <div className={"form-component"}>
              <label>Name</label>
              <CFormInput
                value={tg.name}
                placeholder={"Treatment group name"}
                onChange={e =>
                  updateArm(setter, index, row => ({
                    ...row,
                    qTreatmentGroupNames: row.qTreatmentGroupNames.map(
                      (t, i) =>
                        i === tgIndex ? { ...t, name: e.target.value } : t
                    ),
                  }))
                }
              />
            </div>
            <div className={"form-component"}>
              <label>Role</label>
              <CFormSelect
                value={tg.role}
                options={stringSelectOptions(SCS_TREATMENT_GROUP_ROLE_OPTIONS)}
                onChange={e =>
                  updateArm(setter, index, row => ({
                    ...row,
                    qTreatmentGroupNames: row.qTreatmentGroupNames.map(
                      (t, i) =>
                        i === tgIndex ? { ...t, role: e.target.value } : t
                    ),
                  }))
                }
              />
            </div>
            {arm.qTreatmentGroupNames.length === 1 ? (
              <span className={"empty-space-layout-helper"}></span>
            ) : (
              removeButton("Remove treatment group", () =>
                updateArm(setter, index, row => ({
                  ...row,
                  qTreatmentGroupNames: row.qTreatmentGroupNames.filter(
                    (_, i) => i !== tgIndex
                  ),
                }))
              )
            )}
          </div>
        ))}
        {addButton("Add Treatment Group", () =>
          updateArm(setter, index, row => ({
            ...row,
            qTreatmentGroupNames: [
              ...row.qTreatmentGroupNames,
              emptyTreatmentGroup(),
            ],
          }))
        )}
      </>
    );
    return headingKey
      ? renderQuestion(headingKey, control, numberOverride)
      : renderLabeledBlock("Treatment Group Names", control);
  };

  // Q5 lists the grouping names defined in Q2A.
  const groupingNameOptions = [
    ...new Set(
      groupingDefinitions
        .map(group => (group?.name || "").trim())
        .filter(Boolean)
    ),
  ];

  const renderGroupingNameField = (arm, index, setter, numberOverride) => {
    // One grouping per block; tolerate the list shape held by previously saved
    // state (Q5 was briefly a multi-select).
    const selected = asGroupingName(arm.qGroupingName);
    return renderQuestion(
      "qGroupingName",
      <Select
        className={"scs-dos__control"}
        styles={SINGLE_VALUE_CHIP_STYLES}
        options={toOptions(groupingNameOptions)}
        value={selected ? { value: selected, label: selected } : null}
        onChange={option =>
          updateArm(setter, index, row => ({
            ...row,
            qGroupingName: option ? option.value : "",
          }))
        }
        placeholder={"Select grouping"}
        isClearable
      />,
      numberOverride
    );
  };

  const renderComparisonPairs = (arm, index, setter, numberOverride) => {
    // Always keep at least one pair; the first pair (index 0) is non-removable.
    const basePairs = row =>
      (row.qComparisonPairs || []).length > 0
        ? row.qComparisonPairs
        : [{ treatment: "", comparator: "" }];
    // Q9's dropdowns list the names typed into Q8 for this same grouping
    // (mirrors the CSR 4a -> 4b updateTgNamesComparisonAdditionalData flow).
    const groupNameOptions = stringSelectOptions([
      ...new Set(
        (arm.qTreatmentGroupNames || [])
          .map(tg => (tg?.name || "").trim())
          .filter(Boolean)
      ),
    ]);
    return renderQuestion(
      "qComparisonPairs",
      <>
        {basePairs(arm).map((pair, pairIndex) => (
          <div
            key={`pair-${index}-${pairIndex}`}
            className={"questions-content__input-group"}>
            <div className={"form-component"}>
              <label>Treatment</label>
              <CFormSelect
                value={pair.treatment}
                options={groupNameOptions}
                onChange={e =>
                  updateArm(setter, index, row => ({
                    ...row,
                    qComparisonPairs: basePairs(row).map((p, i) =>
                      i === pairIndex ? { ...p, treatment: e.target.value } : p
                    ),
                  }))
                }
              />
            </div>
            <div className={"form-component"}>
              <label>Comparator</label>
              <CFormSelect
                value={pair.comparator}
                options={groupNameOptions}
                onChange={e =>
                  updateArm(setter, index, row => ({
                    ...row,
                    qComparisonPairs: basePairs(row).map((p, i) =>
                      i === pairIndex ? { ...p, comparator: e.target.value } : p
                    ),
                  }))
                }
              />
            </div>
            {pairIndex === 0 ? (
              <span className={"empty-space-layout-helper"}></span>
            ) : (
              removeButton("Remove comparison pair", () =>
                updateArm(setter, index, row => ({
                  ...row,
                  qComparisonPairs: basePairs(row).filter(
                    (_, i) => i !== pairIndex
                  ),
                }))
              )
            )}
          </div>
        ))}
        {addButton("Add Comparison Pair", () =>
          updateArm(setter, index, row => ({
            ...row,
            qComparisonPairs: [
              ...basePairs(row),
              { treatment: "", comparator: "" },
            ],
          }))
        )}
      </>,
      numberOverride
    );
  };

  const renderNonComparativeGroupNames = (arm, index, setter, numberOverride) =>
    renderQuestion(
      "qNonComparativeGroupNames",
      <>
        {(arm.qNonComparativeGroupNames || []).map((groupName, gnIndex) => (
          <div
            key={`ncgn-${index}-${gnIndex}`}
            className={"questions-content__input-group"}>
            <div className={"form-component"}>
              <CFormInput
                value={groupName}
                placeholder={"Population label"}
                onChange={e =>
                  updateArm(setter, index, row => ({
                    ...row,
                    qNonComparativeGroupNames: (
                      row.qNonComparativeGroupNames || []
                    ).map((g, i) => (i === gnIndex ? e.target.value : g)),
                  }))
                }
              />
            </div>
            {(arm.qNonComparativeGroupNames || []).length === 1 ? (
              <span className={"empty-space-layout-helper"}></span>
            ) : (
              removeButton("Remove group", () =>
                updateArm(setter, index, row => ({
                  ...row,
                  qNonComparativeGroupNames: (
                    row.qNonComparativeGroupNames || []
                  ).filter((_, i) => i !== gnIndex),
                }))
              )
            )}
          </div>
        ))}
        {addButton("Add another", () =>
          updateArm(setter, index, row => ({
            ...row,
            qNonComparativeGroupNames: [
              ...(row.qNonComparativeGroupNames || []),
              "",
            ],
          }))
        )}
      </>,
      numberOverride
    );

  const renderDoseGroups = (arm, index, setter, numberOverride) =>
    renderQuestion(
      "qDoseGroups",
      <>
        {(arm.qDoseGroups || []).map((dose, doseIndex) => (
          <div
            key={`dose-${index}-${doseIndex}`}
            className={"questions-content__input-group"}>
            <div className={"form-component"}>
              <CFormInput
                value={dose}
                placeholder={"Dose group label"}
                onChange={e =>
                  updateArm(setter, index, row => ({
                    ...row,
                    qDoseGroups: (row.qDoseGroups || []).map((d, i) =>
                      i === doseIndex ? e.target.value : d
                    ),
                  }))
                }
              />
            </div>
            {(arm.qDoseGroups || []).length === 1 ? (
              <span className={"empty-space-layout-helper"}></span>
            ) : (
              removeButton("Remove dose group", () =>
                updateArm(setter, index, row => ({
                  ...row,
                  qDoseGroups: (row.qDoseGroups || []).filter(
                    (_, i) => i !== doseIndex
                  ),
                }))
              )
            )}
          </div>
        ))}
        {addButton("Add Dose Group", () =>
          updateArm(setter, index, row => ({
            ...row,
            qDoseGroups: [...(row.qDoseGroups || []), ""],
          }))
        )}
      </>,
      numberOverride
    );

  // A single comparison grouping block (questions 5–9). Rendered on its own
  // when there is one grouping, or inside an accordion item when there are many.
  const renderSingleArmBlock = (arm, index) => {
    const comparisonType = arm.qComparisonType || "";
    const isFirstBlock = index === 0;
    const blockNumber = index + 1;
    // First block keeps the plain page numbers (5..9); every additional
    // block appends "(blockNumber)" to each question index, mirroring the
    // CSR block pattern (e.g. 5(2), 6(2), 8(2)).
    const q = n => (isFirstBlock ? n : `${n}(${blockNumber})`);
    // Branch questions are numbered sequentially from 8 in render order,
    // based on the single selected comparison type.
    let branchNumber = 8;
    const nonComparativeGroupNamesNumber =
      comparisonType === "Non-Comparative" ? q(branchNumber++) : null;
    const treatmentGroupsNumber =
      comparisonType === "Comparative" ? q(branchNumber++) : null;
    const comparisonPairsNumber =
      comparisonType === "Comparative" ? q(branchNumber++) : null;
    const doseGroupsNumber =
      comparisonType === "Dose-Finding" ? q(branchNumber++) : null;
    return (
      <>
        <div className={"scs-dos__block-actions"}>
          {duplicateBlockButton(() => duplicateSingleArm(index))}
          {index > 0 &&
            removeButton("Remove grouping", () => removeSingleArm(index))}
        </div>
        {renderGroupingNameField(arm, index, setSingleArm, q(5))}
        {renderQuestion(
          "qDemPopulation",
          <CFormInput
            value={arm.qDemPopulation || ""}
            placeholder={"e.g. ITT, Full Analysis Set"}
            onChange={e =>
              updateArm(setSingleArm, index, row => ({
                ...row,
                qDemPopulation: e.target.value,
              }))
            }
          />,
          q(6)
        )}
        {renderQuestion(
          "qComparisonType",
          SCS_COMPARISON_TYPE_OPTIONS.map(option => (
            <React.Fragment key={option}>
              <input
                type={"radio"}
                className={"btn-check"}
                name={`comparison-type-${index}`}
                id={`comparison-type-${index}-${option}`}
                autoComplete={"off"}
                checked={comparisonType === option}
                onChange={() =>
                  updateArm(setSingleArm, index, row => ({
                    ...row,
                    qComparisonType: option,
                  }))
                }
              />
              <label
                className={"btn btn-light"}
                htmlFor={`comparison-type-${index}-${option}`}>
                {option}
              </label>
            </React.Fragment>
          )),
          q(7)
        )}
        {comparisonType === "Non-Comparative" &&
          renderNonComparativeGroupNames(
            arm,
            index,
            setSingleArm,
            nonComparativeGroupNamesNumber
          )}
        {comparisonType === "Comparative" && (
          <>
            {renderTreatmentGroups(
              arm,
              index,
              setSingleArm,
              "qTreatmentGroupNames",
              treatmentGroupsNumber
            )}
            {renderComparisonPairs(
              arm,
              index,
              setSingleArm,
              comparisonPairsNumber
            )}
          </>
        )}
        {comparisonType === "Dose-Finding" &&
          renderDoseGroups(arm, index, setSingleArm, doseGroupsNumber)}
      </>
    );
  };

  const renderSingleArm = () => (
    <>
      {singleArm.length === 1 ? (
        <div ref={lastGroupRef} className={"analysis-block"}>
          {renderSingleArmBlock(singleArm[0], 0)}
        </div>
      ) : (
        <>
          <div className={"scs-dos__accordion-controls"}>
            <CButton
              color={"primary"}
              shape={"pill"}
              className={"scs-dos__duplicate"}
              onClick={() =>
                setOpenGroupIndexes(new Set(singleArm.map((_, i) => i)))
              }>
              Expand all
            </CButton>
            <CButton
              color={"primary"}
              shape={"pill"}
              className={"scs-dos__duplicate"}
              onClick={() => setOpenGroupIndexes(new Set())}>
              Collapse all
            </CButton>
          </div>
          <div className={"accordion"}>
            {singleArm.map((arm, index) => {
              const isOpen = openGroupIndexes.has(index);
              const isLast = index === singleArm.length - 1;
              return (
                <div
                  key={`single-${index}`}
                  className={"accordion-item"}
                  ref={isLast ? lastGroupRef : null}>
                  <div className={"accordion-header"}>
                    <button
                      type={"button"}
                      className={`accordion-button${isOpen ? "" : " collapsed"}`}
                      onClick={() => toggleGroup(index)}>
                      Grouping {index + 1}
                    </button>
                  </div>
                  <CCollapse visible={isOpen} className={"accordion-collapse"}>
                    <div className={"accordion-body"}>
                      <div className={"analysis-block"}>
                        {renderSingleArmBlock(arm, index)}
                      </div>
                    </div>
                  </CCollapse>
                </div>
              );
            })}
          </div>
        </>
      )}
      <div className={"scs-dos__block-footer"}>
        {addButton("Add another group", addSingleArm)}
        {duplicateAboveButton("Duplicate group above", duplicateSingleArmAbove)}
      </div>
    </>
  );

  const PageContent = () => (
    <div className={"questions-page scs-dos"}>
      <div className={"questions-content"}>
        <div className={"questions-content__items"}>
          <ToastContainer />
          <form id={FORM_ID} onSubmit={handleSubmit}>
            {renderStudies()}
            {renderGroupings()}
            {renderDescriptions()}
            {renderSingleArm()}
          </form>
          <Tooltip
            id={REMOVE_TOOLTIP_ID}
            place={"top"}
            delayShow={100}
            className={"custom-tooltip"}
          />
          <Tooltip
            id={DUPLICATE_TOOLTIP_ID}
            place={"top"}
            delayShow={100}
            className={"custom-tooltip"}
          />
        </div>
      </div>
    </div>
  );

  if (showRedirect || wrongEnvironment) {
    return <RedirectHandler />;
  }

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Description of Studies"}
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "File Upload",
        buttonGoToAction: `/scs/${summaryId}/file-upload`,
      }}
      saveButton={true}
      saveButtonHandler={handleSave}
      saveButtonDisabled={isSubmitting}
      nextButton={{
        buttonTitle: isSubmitting ? "Saving..." : "Subject Disposition",
        buttonFormAction: FORM_ID,
        buttonGoToAction: `/scs/${summaryId}/subject-disposition`,
        disabled: isSubmitting,
      }}
    />
  );
};

export default SCSDescriptionOfStudies;
